import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import type {
  MySqlQueryListItem,
  MySqlQueryListPage,
  MySqlQueryListParams,
  MySqlQueryRecordDeleteResult,
  MySqlQueryRecordMutation,
} from "../mysql-query-list/types";
import {
  mapConnectivityPayload,
  mapMySqlQueryListItem,
  mapMySqlQueryListPage,
  mapMySqlQueryListQueryParams,
  mapMySqlQueryRecordMutationPayload,
  type DatabaseConnectivityRaw,
  type DatabaseConnectivityResult,
  type DatabaseType,
  type MySqlQueryListItemRaw,
  type MySqlQueryListPageRaw,
  type MySqlQueryRecordDeleteResultRaw,
  type MySqlQueryRecordId,
  type SqlServerConnectivityResult,
} from "./adapters";

export type {
  DatabaseConnectivityResult,
  DatabaseType,
  MySqlQueryListItem,
  MySqlQueryListPage,
  MySqlQueryListParams,
  MySqlQueryRecordDeleteResult,
  MySqlQueryRecordMutation,
  SqlServerConnectivityResult,
};

/**
 * 调用后端 Demo 接口，获取 SQL Server 连通性探测结果。
 */
export async function fetchSqlServerConnectivity(
  customerId: string,
): Promise<SqlServerConnectivityResult> {
  const response = await httpClient.get<ApiEnvelope<DatabaseConnectivityRaw>>(
    "/demo/sqlserver/connectivity",
    {
      params: { customer_id: customerId },
    },
  );

  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("Demo 接口返回了无效数据");
  }

  return mapConnectivityPayload(response.data.data, "sqlserver");
}

/**
 * 调用后端 Demo 接口，获取 MySQL 连通性探测结果。
 */
export async function fetchMySqlConnectivity(
  customerId: string,
): Promise<DatabaseConnectivityResult> {
  const response = await httpClient.get<ApiEnvelope<DatabaseConnectivityRaw>>(
    "/demo/mysql/connectivity",
    {
      params: { customer_id: customerId },
    },
  );

  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("Demo 接口返回了无效数据");
  }

  return mapConnectivityPayload(response.data.data, "mysql");
}

/**
 * 按数据库类型分发连通性请求。
 */
export async function fetchDatabaseConnectivity(
  databaseType: DatabaseType,
  customerId: string,
): Promise<DatabaseConnectivityResult> {
  if (databaseType === "mysql") {
    return fetchMySqlConnectivity(customerId);
  }
  return fetchSqlServerConnectivity(customerId);
}

/**
 * 调用后端心跳接口，校验服务可达性。
 */
export async function fetchHeartbeat(): Promise<void> {
  const response = await httpClient.get<ApiEnvelope<null>>("/healthz");

  if (response.data.code !== "OK") {
    throw new Error("心跳接口返回了无效数据");
  }
}

/**
 * 调用后端 Demo 接口，获取 MySQL 分页查询列表。
 */
export async function fetchMySqlQueryList(
  params: MySqlQueryListParams,
): Promise<MySqlQueryListPage> {
  const response = await httpClient.get<ApiEnvelope<MySqlQueryListPageRaw>>(
    "/demo/mysql/query-list",
    {
      params: mapMySqlQueryListQueryParams(params),
    },
  );

  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("MySQL 分页查询接口返回了无效数据");
  }

  return mapMySqlQueryListPage(response.data.data);
}

/**
 * 调用后端 Demo2 接口，获取 MySQL 分页查询列表。
 */
export async function fetchMySqlQueryListDemo2(
  params: MySqlQueryListParams,
): Promise<MySqlQueryListPage> {
  const response = await httpClient.get<ApiEnvelope<MySqlQueryListPageRaw>>(
    "/demo/mysql/query-list-demo2",
    {
      params: mapMySqlQueryListQueryParams(params),
    },
  );

  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("MySQL Demo2 分页查询接口返回了无效数据");
  }

  return mapMySqlQueryListPage(response.data.data);
}

/**
 * 调用后端 Demo 接口，创建 MySQL 记录。
 */
export async function createMySqlQueryRecord(
  payload: MySqlQueryRecordMutation,
): Promise<MySqlQueryListItem> {
  const response = await httpClient.post<ApiEnvelope<MySqlQueryListItemRaw>>(
    "/demo/mysql/query-records",
    mapMySqlQueryRecordMutationPayload(payload),
  );

  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("MySQL 记录创建接口返回了无效数据");
  }

  return mapMySqlQueryListItem(response.data.data);
}

/**
 * 调用后端 Demo 接口，更新 MySQL 记录。
 */
export async function updateMySqlQueryRecord(
  recordId: MySqlQueryRecordId,
  payload: MySqlQueryRecordMutation,
): Promise<MySqlQueryListItem> {
  const response = await httpClient.put<ApiEnvelope<MySqlQueryListItemRaw>>(
    `/demo/mysql/query-records/${recordId}`,
    mapMySqlQueryRecordMutationPayload(payload),
  );

  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("MySQL 记录更新接口返回了无效数据");
  }

  return mapMySqlQueryListItem(response.data.data);
}

/**
 * 调用后端 Demo 接口，逻辑删除 MySQL 记录。
 */
export async function deleteMySqlQueryRecord(
  recordId: MySqlQueryRecordId,
): Promise<MySqlQueryRecordDeleteResult> {
  const response = await httpClient.delete<ApiEnvelope<MySqlQueryRecordDeleteResultRaw>>(
    `/demo/mysql/query-records/${recordId}`,
  );

  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("MySQL 记录删除接口返回了无效数据");
  }

  return response.data.data;
}
